/*
 * PDP "In Store" panel - per-store list of which variants are
 * actually pickup-able today at our two physical stores (Łódź /
 * Składowa, Warsaw / Mokotowska). Each store section shows only the
 * variants that have at least one unit AT THAT STORE - asking
 * "what can I pick up here" rather than rendering a full grid of
 * mostly zeros.
 *
 * Data source: per-variant JSON map emitted by
 * pdp-variant-picker.liquid as <script data-store-inventory>:
 *   { "<variantId>": { "title": "S", "available": true,
 *                      "stock": {"lodz":N,"warszawa":M} | null } }
 *
 * The currently selected variant gets the `.is-current` chip in
 * every store where it has stock. If it's not stocked anywhere
 * the chip just doesn't appear (its info is "not in any store").
 *
 * Why not subscribe to PUB_SUB_EVENTS.variantChange directly: those
 * symbols are file-scoped to t-product.js and not exposed on `window`,
 * so a separate module can't import them without modifying that file.
 * Instead we observe the `[data-selected-variant]` script that the
 * theme already maintains - on every variant change t-product.js
 * replaces the variant-selects subtree (see t-product.js:1224
 * updateOptionValues), which mutates the script content and fires
 * the observer.
 */

(() => {
    'use strict'

    const STORES = ['lodz', 'warszawa']

    const safeJsonParse = (str) => {
        if (!str) return null
        try { return JSON.parse(str) } catch { return null }
    }

    function buildStoreSection(section, rows) {
        const list = section.querySelector('[data-instore-sizes]')
        const empty = section.querySelector('[data-instore-store-empty]')
        if (!list || !empty) return

        if (rows.length === 0) {
            list.replaceChildren()
            list.hidden = true
            empty.hidden = false
            return
        }

        list.hidden = false
        empty.hidden = true

        const frag = document.createDocumentFragment()
        for (const r of rows) {
            const li = document.createElement('li')
            li.className = 'instore__size'
            li.dataset.variantId = r.variantId

            const label = document.createElement('span')
            label.className = 'instore__size-label'
            label.textContent = r.title
            li.appendChild(label)

            const qty = document.createElement('span')
            qty.className = 'instore__size-qty'
            qty.textContent = String(r.qty)
            li.appendChild(qty)

            frag.appendChild(li)
        }
        list.replaceChildren(frag)
    }

    function initPanel(panel) {
        const sectionId = panel.dataset.sectionId
        if (!sectionId) return

        // Find a stable parent: product-info is the custom element that
        // wraps variant-selects and does NOT get swapped on variant
        // change (the swap target is the variant-selects subtree
        // inside it via HTMLUpdateUtility.viewTransition). Observing
        // variant-selects directly stops working after the first
        // variant change because the node we attached to is removed.
        const productInfo = document.getElementById(`MainProduct-${sectionId}`)
        if (!productInfo) return

        function getVariantSelects() {
            return productInfo.querySelector(
                `variant-selects[data-section="${sectionId}"]`,
            )
        }

        const initialVs = getVariantSelects()
        if (!initialVs) return

        const mapScript = initialVs.querySelector('[data-store-inventory]')
        const map = safeJsonParse(mapScript?.textContent) || {}

        // Build per-store row sets up front. Map is product-static so
        // we only need to do this once on mount.
        const storeSections = {}
        for (const store of STORES) {
            const section = panel.querySelector(
                `.instore__store[data-store="${store}"]`,
            )
            if (!section) continue
            storeSections[store] = section

            const rows = []
            for (const [variantId, entry] of Object.entries(map)) {
                const qty = Number(entry?.stock?.[store] ?? 0)
                if (qty <= 0) continue
                rows.push({
                    variantId,
                    title: String(entry?.title ?? variantId),
                    qty,
                })
            }
            buildStoreSection(section, rows)
        }

        function highlightCurrent() {
            // Re-query the variant-selects node every time — it gets
            // fully replaced by HTMLUpdateUtility.viewTransition on
            // variant change, so any cached reference becomes stale.
            const vs = getVariantSelects()
            const variantScript = vs?.querySelector('[data-selected-variant]')
            const variant = safeJsonParse(variantScript?.textContent)
            const currentId = variant?.id != null ? String(variant.id) : null
            for (const store of STORES) {
                const section = storeSections[store]
                if (!section) continue
                section
                    .querySelectorAll('.instore__size[data-variant-id]')
                    .forEach((li) =>
                        li.classList.toggle(
                            'is-current',
                            li.dataset.variantId === currentId,
                        ),
                    )
            }
        }

        highlightCurrent()

        // Observe the stable parent. childList+subtree catches both
        // the variant-selects replacement AND the inner text-node
        // updates of [data-selected-variant]. characterData ensures we
        // also catch in-place script-tag content updates if Shopify
        // ever decides to patch instead of replace.
        const mo = new MutationObserver(highlightCurrent)
        mo.observe(productInfo, {
            childList: true,
            subtree: true,
            characterData: true,
        })

        // Defensive re-read on open in case mutations were missed
        // while the dialog was closed (cheap, just toggles classes).
        const dialog = panel.querySelector('dialog')
        dialog?.addEventListener('toggle', () => {
            if (dialog.open) highlightCurrent()
        })
    }

    function init() {
        document
            .querySelectorAll('.coretex-dialog[id^="instore-"]')
            .forEach(initPanel)
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init, { once: true })
    } else {
        init()
    }
})()
